import client from '@/services/http/client'
import { isMockEnabled, mockDelay, paginate, sortByParam } from '@/services/http/mock'
import type { PaginatedData } from '@/types/api'
import type { DraftItem, DraftDetail, DraftTriggerSource, AssignmentCandidate, TriggerContext } from '@/types/draft'
import { normalizeMessageMedia } from './messages'

// ============== Mock 数据 ==============
const mockDrafts: DraftItem[] = [
  {
    id: 'draft-001',
    draft_no: 'DF-20260514-001',
    customer_name: '某医院',
    project_name: '某医院HIS系统',
    task_title: '排查-报到机-查询排班提示未在班',
    problem_type: '现场故障',
    priority: 'P2',
    status: 'drafted',
    confidence: 0.85,
    risk_flags: ['信息不足'],
    created_at: '2026-05-14T10:11:00+08:00',
    updated_at: '2026-05-14T10:15:00+08:00',
  },
  {
    id: 'draft-002',
    draft_no: 'DF-20260514-002',
    customer_name: '某客户',
    project_name: '某项目Portal',
    task_title: '修复-登录认证异常',
    problem_type: '系统异常',
    priority: 'P1',
    status: 'need_more_info',
    confidence: 0.62,
    risk_flags: ['低置信度'],
    created_at: '2026-05-14T10:29:00+08:00',
    updated_at: '2026-05-14T10:30:00+08:00',
    trigger_source: { source_type: 'wechat_group_trigger', trigger_key: 'room:portal-support:msg-002' },
  },
  {
    id: 'draft-003',
    draft_no: 'DF-20260514-003',
    customer_name: '某医院',
    project_name: '某医院HIS系统',
    task_title: '排查-打印机E-500错误',
    problem_type: '硬件故障',
    priority: 'P2',
    status: 'drafted',
    confidence: 0.91,
    risk_flags: [],
    created_at: '2026-05-14T10:01:00+08:00',
    updated_at: '2026-05-14T10:05:00+08:00',
  },
  {
    id: 'draft-004',
    draft_no: 'DF-20260514-004',
    customer_name: '',
    project_name: '',
    task_title: '排查-叫号系统响应缓慢',
    problem_type: '性能问题',
    priority: 'P3',
    status: 'binding_required',
    confidence: 0.45,
    risk_flags: ['绑定缺失', '低置信度'],
    created_at: '2026-05-14T09:08:00+08:00',
    updated_at: '2026-05-14T09:10:00+08:00',
  },
]

const mockDraftDetail: Record<string, DraftDetail> = {
  'draft-001': {
    id: 'draft-001',
    draft_no: 'DF-20260514-001',
    customer_name: '某医院',
    project_name: '某医院HIS系统',
    task_title: '排查-报到机-查询排班提示未在班',
    summary: '某医院报到机在查询排班时提示未在班，无法正常使用报到功能。',
    problem_type: '现场故障',
    need_ticket: true,
    is_out_of_warranty: false,
    priority: 'P2',
    status: 'drafted',
    confidence: 0.85,
    risk_flags: ['信息不足'],
    source_messages: [
      { message_id: 'msg-001', sender_name: '张三', content_preview: '报到机查询排班提示未在班，无法正常使用，请协助排查。', conversation_items: [], send_time: '2026-05-14T10:10:00+08:00', media: [] },
    ],
    binding: {
      room_name: '某医院售后群',
      customer_name: '某医院',
      project_name: '某医院HIS系统',
      zentao_product_id: 4,
      zentao_project_id: 101,
      zentao_execution_id: 'exec-001',
      default_execution_id: 202,
    },
    execution_routing: {
      suggested_execution_name: '报到机-售后-2026Q2',
      zentao_project_id: 101,
      default_execution_id: 202,
      resolution_status: 'matched_existing',
      candidates: [
        { execution_id: 303, project_id: 101, name: '报到机-售后-2026Q2', status: 'doing' },
      ],
    },
    known_facts: ['报到机触屏正常，但查询排班时提示"未在班"'],
    missing_info: ['请补充发生时间、具体科室、是否所有报到机都出现该问题'],
    acceptance_criteria: ['报到机能正常查询排班并完成报到'],
    suggested_reply: '已收到您的反馈，我们正在跟进排查，请补充发生的具体时间和影响范围。',
    human_assignee: '',
    expected_finish_at: '',
    status_events: [
      { from_status: '', to_status: 'drafted', actor: 'system', note: 'AI自动生成草稿', created_at: '2026-05-14T10:11:00+08:00' },
    ],
    operator_actions: ['update', 'ask_more', 'reject', 'confirm'],
    trigger_source: null,
    assigned_to: '',
    draft_payload: {
      create_task_command_snapshot: {
        zentao_execution_id: 'exec-001',
        priority: 'P2',
        problem_type: '现场故障',
        summary: '某医院报到机在查询排班时提示未在班，无法正常使用报到功能。',
        assigned_to: '',
        human_assignee: '',
        known_facts: ['报到机触屏正常，但查询排班时提示"未在班"'],
        missing_info: ['请补充发生时间、具体科室、是否所有报到机都出现该问题'],
        acceptance_criteria: ['报到机能正常查询排班并完成报到'],
      },
    },
  },
  'draft-002': {
    id: 'draft-002',
    draft_no: 'DF-20260514-002',
    customer_name: '某客户',
    project_name: '某项目Portal',
    task_title: '修复-登录认证异常',
    summary: '微信群内触发建单，登录认证偶发失败，需要前端先确认触发来源和上下文消息。 ',
    problem_type: '系统异常',
    need_ticket: true,
    is_out_of_warranty: false,
    priority: 'P1',
    status: 'need_more_info',
    confidence: 0.62,
    risk_flags: ['低置信度'],
    source_messages: [
      {
        message_id: 'msg-002',
        sender_name: '李四',
        content_preview: '登录时提示认证失败，请协助排查。',
        conversation_items: [],
        send_time: '2026-05-14T10:28:00+08:00',
        media: [],
      },
    ],
    binding: {
      room_name: '某项目售后群',
      customer_name: '某客户',
      project_name: '某项目Portal',
      zentao_product_id: 67,
      zentao_project_id: 102,
      zentao_execution_id: 'exec-002',
      default_execution_id: 0,
    },
    execution_routing: {
      suggested_execution_name: '系统-售后-2026Q2',
      zentao_project_id: 102,
      default_execution_id: 0,
      resolution_status: 'will_create_execution',
      candidates: [],
    },
    known_facts: ['问题由微信群触发，当前仅 1 条触发消息进入草稿流程'],
    missing_info: ['请补充失败账号、发生频次、是否影响全部用户'],
    acceptance_criteria: ['用户可正常完成登录认证，不再出现认证失败提示'],
    suggested_reply: '已收到反馈，请补充失败账号和发生时间，我们先按触发消息定位问题范围。',
    human_assignee: 'tech.lead',
    expected_finish_at: '',
    status_events: [
      {
        from_status: '',
        to_status: 'need_more_info',
        actor: 'system',
        note: '微信群触发后生成草稿',
        created_at: '2026-05-14T10:29:00+08:00',
      },
    ],
    operator_actions: ['update', 'ask_more', 'reject', 'confirm'],
    trigger_source: {
      source_type: 'wechat_group_trigger',
      trigger_message_id: 'wx-msg-trigger-002',
      context_message_id: 'wx-msg-context-001',
      trigger_key: 'room:portal-support:msg-002',
    },
    assigned_to: 'tech.lead',
    draft_payload: {
      create_task_command_snapshot: {
        zentao_execution_id: 'exec-002',
        priority: 'P2',
        problem_type: '系统异常',
        summary: '微信群内触发建单，登录认证偶发失败，需要前端先确认触发来源和上下文消息。',
        assigned_to: 'tech.lead',
        human_assignee: 'tech.lead',
        known_facts: ['问题由微信群触发，当前仅 1 条触发消息进入草稿流程'],
        missing_info: ['请补充失败账号、发生频次、是否影响全部用户'],
        acceptance_criteria: ['用户可正常完成登录认证，不再出现认证失败提示'],
      },
    },
  },
}

// ============== Adapter: 后端 → 前端 UI model ==============
function toStrArray(v: unknown): string[] {
  if (Array.isArray(v)) return v as string[]
  if (typeof v === 'string' && v.length > 0) return [v]
  return []
}

function toRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
}

function toOptionalString(v: unknown): string {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : ''
}

function normalizeDraftTriggerSource(
  draftPayload: Record<string, unknown> | null,
): DraftTriggerSource | null {
  if (!draftPayload || draftPayload.source !== 'wechat_group_trigger') {
    return null
  }

  const metadata = toRecord(draftPayload.metadata)
  const triggerMessageId = toOptionalString(
    draftPayload.trigger_message_id ?? metadata?.trigger_message_id,
  )
  const contextMessageId = toOptionalString(
    draftPayload.context_message_id ??
      metadata?.context_message_id ??
      draftPayload.context_trigger_message_id ??
      metadata?.context_trigger_message_id,
  )
  const triggerKey = toOptionalString(draftPayload.trigger_key ?? metadata?.trigger_key)

  if (!triggerMessageId && !contextMessageId && !triggerKey) {
    return null
  }

  return {
    source_type: 'wechat_group_trigger',
    trigger_message_id: triggerMessageId,
    context_message_id: contextMessageId,
    trigger_key: triggerKey,
  }
}

export function normalizeDraftDetail(raw: unknown): DraftDetail {
  if (!raw || typeof raw !== 'object') throw new Error('Invalid draft detail response')
  const r = raw as Record<string, unknown>
  const draftPayload = toRecord(r.draft_payload)
  const routing = toRecord(r.execution_routing)

  const sourceMessages = Array.isArray(r.source_messages)
    ? (r.source_messages as Array<Record<string, unknown>>).map((msg) => ({
        message_id: String(msg.message_id ?? ''),
        sender_name: String(msg.sender_name ?? msg.sender ?? ''),
        // 后端字段 content → 前端 content_preview
        content_preview: String(msg.content_preview ?? msg.content ?? ''),
        conversation_items: Array.isArray(msg.conversation_items)
          ? (msg.conversation_items as Array<Record<string, unknown>>).map((item) => ({
              sender_name: String(item.sender_name ?? item.sender ?? ''),
              content: String(item.content ?? item.content_preview ?? ''),
            })).filter((item) => item.content.trim())
          : [],
        send_time: String(msg.send_time ?? ''),
        media: Array.isArray(msg.media) ? msg.media.map((item) => normalizeMessageMedia(item)) : [],
      }))
    : []

  return {
    id: String(r.id ?? r.draft_id ?? ''),
    draft_no: String(r.draft_no ?? ''),
    customer_name: String(r.customer_name ?? ''),
    project_name: String(r.project_name ?? ''),
    task_title: String(r.task_title ?? ''),
    summary: String(r.summary ?? ''),
    problem_type: String(r.problem_type ?? ''),
    need_ticket: Boolean(r.need_ticket ?? false),
    is_out_of_warranty: Boolean(r.is_out_of_warranty ?? false),
    priority: String(r.priority ?? ''),
    status: String(r.status ?? ''),
    confidence: Number(r.confidence ?? 0),
    risk_flags: Array.isArray(r.risk_flags) ? (r.risk_flags as string[]) : [],
    source_messages: sourceMessages,
    binding: r.binding
      ? {
          room_name: String((r.binding as Record<string, unknown>).room_name ?? ''),
          customer_name: String((r.binding as Record<string, unknown>).customer_name ?? ''),
          project_name: String((r.binding as Record<string, unknown>).project_name ?? ''),
          zentao_product_id: Number((r.binding as Record<string, unknown>).zentao_product_id ?? 0),
          zentao_project_id: Number((r.binding as Record<string, unknown>).zentao_project_id ?? 0),
          zentao_execution_id: String((r.binding as Record<string, unknown>).zentao_execution_id ?? ''),
          default_execution_id: Number((r.binding as Record<string, unknown>).default_execution_id ?? (r.binding as Record<string, unknown>).zentao_execution_id ?? 0),
        }
      : null,
    execution_routing: routing
      ? {
          suggested_execution_name: String(routing.suggested_execution_name ?? ''),
          zentao_project_id: Number(routing.zentao_project_id ?? 0),
          default_execution_id: Number(routing.default_execution_id ?? 0),
          resolution_status: String(routing.resolution_status ?? ''),
          candidates: Array.isArray(routing.candidates)
            ? (routing.candidates as Array<Record<string, unknown>>).map((item) => ({
                execution_id: Number(item.execution_id ?? 0),
                project_id: Number(item.project_id ?? 0),
                name: String(item.name ?? ''),
                status: String(item.status ?? ''),
              }))
            : [],
        }
      : null,
    known_facts: toStrArray(r.known_facts),
    missing_info: toStrArray(r.missing_info),
    acceptance_criteria: toStrArray(r.acceptance_criteria),
    suggested_reply: String(r.suggested_reply ?? ''),
    human_assignee: String(r.human_assignee ?? ''),
    expected_finish_at: String(r.expected_finish_at ?? ''),
    status_events: Array.isArray(r.status_events)
      ? (r.status_events as DraftDetail['status_events'])
      : [],
    operator_actions: Array.isArray(r.operator_actions)
      ? (r.operator_actions as string[])
      : [],
    trigger_source: normalizeDraftTriggerSource(draftPayload),
    assigned_to: String(r.assigned_to ?? r.suggested_assignee ?? ''),
    draft_payload: (r.draft_payload as DraftDetail['draft_payload']) ?? null,
  }
}

// ============== API 方法 ==============
export async function fetchDrafts(params: {
  page?: number
  page_size?: number
  status?: string
  keyword?: string
  sort_by?: string
  sort_order?: string
}): Promise<PaginatedData<DraftItem>> {
  if (isMockEnabled()) {
    await mockDelay()
    let filtered = [...mockDrafts]
    if (params.status) {
      filtered = filtered.filter((d) => d.status === params.status)
    }
    if (params.keyword) {
      const kw = params.keyword.toLowerCase()
      filtered = filtered.filter(
        (d) =>
          d.customer_name.toLowerCase().includes(kw) ||
          d.task_title.toLowerCase().includes(kw)
      )
    }
    filtered = sortByParam(filtered, params.sort_by, params.sort_order, {
      created_at: 'created_at',
      updated_at: 'updated_at',
      confidence: 'confidence',
    })
    return paginate(filtered, params.page || 1, params.page_size || 20)
  }
  const { data } = await client.get<PaginatedData<DraftItem>>('/ticket-drafts', { params })
  return data
}

export async function fetchDraftDetail(draftId: string): Promise<DraftDetail> {
  if (isMockEnabled()) {
    await mockDelay()
    const detail = mockDraftDetail[draftId]
    if (!detail) {
      // 兜底：基于列表项构造
      const item = mockDrafts.find((d) => d.id === draftId)
      if (!item) throw Object.assign(new Error('草稿不存在'), { code: 'NOT_FOUND' })
      const fallback: DraftDetail = {
        ...item,
        need_ticket: item.problem_type === '现场故障',
        is_out_of_warranty: false,
        summary: '',
        source_messages: [],
        binding: null,
        execution_routing: null,
        known_facts: [],
        missing_info: [],
        acceptance_criteria: [],
        suggested_reply: '',
        human_assignee: '',
        expected_finish_at: '',
        status_events: [],
        operator_actions: [],
        trigger_source: null,
        assigned_to: '',
        draft_payload: null,
      }
      return fallback
    }
    return { ...detail }
  }
  const { data } = await client.get(`/ticket-drafts/${draftId}`)
  return normalizeDraftDetail(data)
}

export async function saveDraft(draftId: string, payload: Record<string, unknown>): Promise<DraftDetail> {
  if (isMockEnabled()) {
    await mockDelay()
    return mockDraftDetail[draftId] || ({} as DraftDetail)
  }
  const { data } = await client.put<DraftDetail>(`/ticket-drafts/${draftId}`, payload)
  return data
}

export async function confirmDraft(draftId: string, payload: Record<string, unknown>): Promise<void> {
  if (isMockEnabled()) {
    await mockDelay()
    return
  }
  await client.post(`/ticket-drafts/${draftId}/confirm`, payload)
}

export async function askMoreDraft(draftId: string, payload: Record<string, unknown>): Promise<void> {
  if (isMockEnabled()) {
    await mockDelay()
    if (mockDraftDetail[draftId]) {
      mockDraftDetail[draftId] = {
        ...mockDraftDetail[draftId],
        status: 'need_more_info',
        missing_info: Array.isArray(payload.missing_info)
          ? (payload.missing_info as string[])
          : mockDraftDetail[draftId].missing_info,
        suggested_reply:
          typeof payload.suggested_reply === 'string'
            ? payload.suggested_reply
            : mockDraftDetail[draftId].suggested_reply,
      }
    }
    return
  }
  await client.post(`/ticket-drafts/${draftId}/ask-more`, payload)
}

export async function rejectDraft(draftId: string, payload: Record<string, unknown>): Promise<void> {
  if (isMockEnabled()) {
    await mockDelay()
    if (mockDraftDetail[draftId]) {
      mockDraftDetail[draftId] = {
        ...mockDraftDetail[draftId],
        status: 'rejected',
      }
    }
    return
  }
  await client.post(`/ticket-drafts/${draftId}/reject`, payload)
}

export interface GenerateDraftResult {
  id: string
  draft_no: string
  status: string
}

function normalizeGenerateDraftResult(raw: unknown): GenerateDraftResult {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid generate draft response')
  }

  const r = raw as Record<string, unknown>
  return {
    id: String(r.id ?? r.draft_id ?? ''),
    draft_no: String(r.draft_no ?? ''),
    status: String(r.status ?? ''),
  }
}

// ============== 研发分配候选 mock ==============
const mockCandidates: Record<string, AssignmentCandidate[]> = {
  'draft-001': [
    { account: 'tech.lead', internal_account: 'tech_1', zentao_account: 'tech.lead', realname: '张三', source: 'configured_internal', reason: '系统已配置研发', valid: true, risk_flags: [] },
    { account: 'dev.a', internal_account: 'tech_2', zentao_account: 'dev.a', realname: '李四', source: 'history', reason: '历史处理过同类问题', valid: true, risk_flags: ['负载较高'] },
    { account: 'dev.b', internal_account: 'tech_3', zentao_account: 'dev.b', realname: '王五', source: 'configured_internal', reason: '系统已配置研发', valid: false, risk_flags: ['已离职'] },
  ],
  'draft-002': [
    { account: 'tech.lead', internal_account: 'tech_4', zentao_account: 'tech.lead', realname: '赵六', source: 'configured_internal', reason: '系统已配置研发', valid: true, risk_flags: [] },
  ],
}

const mockTriggerContexts: Record<string, TriggerContext> = {
  'draft-002': {
    room_id: 'room:portal-support',
    trigger_key: 'room:portal-support:msg-002',
    trigger_message: {
      message_id: 'wx-msg-trigger-002',
      content_preview: '登录时提示认证失败，请协助排查。',
      send_time: '2026-05-14T10:28:00+08:00',
    },
    context_message: {
      message_id: 'wx-msg-context-001',
      content_preview: '[合并转发] 用户反馈早上10点后登录不稳定，涉及Portal和HIS系统',
      send_time: '2026-05-14T10:27:00+08:00',
    },
    forward_summary: '合并转发消息：登录认证异常讨论（2条消息）',
  },
}

// ============== API 方法（新增） ==============

/** 获取研发分配候选池 */
export async function fetchAssignmentCandidates(draftId: string): Promise<AssignmentCandidate[]> {
  if (isMockEnabled()) {
    await mockDelay()
    return mockCandidates[draftId] || []
  }
  const { data } = await client.get<
    AssignmentCandidate[] | { draft_id: string | number; candidates?: AssignmentCandidate[] }
  >(`/ticket-drafts/${draftId}/assignment-candidates`)
  return Array.isArray(data) ? data : data.candidates || []
}

/** 获取触发消息上下文 */
export async function fetchTriggerContext(draftId: string): Promise<TriggerContext | null> {
  if (isMockEnabled()) {
    await mockDelay()
    return mockTriggerContexts[draftId] || null
  }
  void draftId
  return null
}

/** 创建禅道 task（确认草稿后调用的第二步） */
export async function createZentaoTask(draftId: string, payload: Record<string, unknown>): Promise<{ mapping_id: string; zentao_task_id: number }> {
  if (isMockEnabled()) {
    await mockDelay()
    return { mapping_id: `map-${Date.now()}`, zentao_task_id: Math.floor(10000 + Math.random() * 90000) }
  }
  const { data } = await client.post<{ mapping_id: string; zentao_task_id: number }>(`/zentao/tasks/from-draft/${draftId}`, payload)
  return data
}

export async function generateDraft(messageId: string): Promise<GenerateDraftResult> {
  if (isMockEnabled()) {
    await mockDelay()
    const draftNo = `DF-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(mockDrafts.length + 1).padStart(3, '0')}`
    const newDraft: DraftItem = {
      id: `draft-gen-${Date.now()}`,
      draft_no: draftNo,
      customer_name: '',
      project_name: '',
      task_title: '待处理问题',
      problem_type: '现场故障',
      priority: 'P2',
      status: 'drafted',
      confidence: 0.8,
      risk_flags: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
    mockDrafts.unshift(newDraft)
    return normalizeGenerateDraftResult(newDraft)
  }
  const { data } = await client.post(
    '/ticket-drafts/generate',
    { message_ids: [messageId] },
    { timeout: 45_000 },
  )
  return normalizeGenerateDraftResult(data)
}
